/**
 * Página de Teste de Carga - Dashboard de Monitoramento
 * 
 * Permite:
 * - Configurar e iniciar testes de carga
 * - Gerar contatos de teste
 * - Monitorar métricas em tempo real
 * - Visualizar histórico de testes
 * - Limpar dados de teste
 * - MODO FLUXO: Simular interação com fluxos automatizados
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { 
  Play, 
  Square, 
  Users, 
  Trash2, 
  Activity, 
  Clock, 
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  BarChart3,
  RefreshCw,
  Workflow,
  RotateCcw,
} from 'lucide-react';
import { 
  useStressTestRuns, 
  useStressTestDetails, 
  useStressTestMetricsRealtime,
  useStressTestController,
  useQueueSize,
  type StressTestConfig,
} from '@/hooks/useStressTest';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

/**
 * Componente de cartão de estatística
 */
function StatCard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  trend,
  variant = 'default' 
}: { 
  title: string; 
  value: string | number; 
  subtitle?: string;
  icon: React.ElementType;
  trend?: 'up' | 'down' | 'neutral';
  variant?: 'default' | 'success' | 'warning' | 'error';
}) {
  const variantStyles = {
    default: 'text-muted-foreground',
    success: 'text-green-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${variantStyles[variant]}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Badge de status do teste
 */
function TestStatusBadge({ status }: { status: string }) {
  const statusConfig = {
    pending: { label: 'Pendente', variant: 'secondary' as const, icon: Clock },
    running: { label: 'Executando', variant: 'default' as const, icon: Loader2 },
    completed: { label: 'Concluído', variant: 'outline' as const, icon: CheckCircle },
    failed: { label: 'Falhou', variant: 'destructive' as const, icon: XCircle },
    cancelled: { label: 'Cancelado', variant: 'secondary' as const, icon: Square },
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
}

export default function StressTest() {
  // Estado do formulário de configuração
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedInbox, setSelectedInbox] = useState<string>('');
  const [messagesPerSecond, setMessagesPerSecond] = useState(10);
  const [durationSeconds, setDurationSeconds] = useState(60);
  const [contactCount, setContactCount] = useState(10000);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  // Estado controlado da aba ativa para navegação automática
  const [activeTab, setActiveTab] = useState<string>('config');
  
  // Estados para modo de fluxo
  const [flowModeEnabled, setFlowModeEnabled] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');
  const [responseIntervalMin, setResponseIntervalMin] = useState(2);
  const [responseIntervalMax, setResponseIntervalMax] = useState(5);
  const [maxInteractionsPerFlow, setMaxInteractionsPerFlow] = useState(10);

  // Hooks de dados
  const { data: testRuns, isLoading: runsLoading, refetch: refetchRuns } = useStressTestRuns(20);
  const { data: testDetails, isLoading: detailsLoading } = useStressTestDetails(selectedTestId);
  const metrics = useStressTestMetricsRealtime(selectedTestId);
  const { data: queueSize } = useQueueSize();
  const { startTest, stopTest, generateContacts, cleanup, resetInbox } = useStressTestController();

  // Buscar tenants para seleção
  const { data: tenants } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Buscar inboxes do tenant selecionado
  const { data: inboxes } = useQuery({
    queryKey: ['tenant-inboxes', selectedTenant],
    queryFn: async () => {
      if (!selectedTenant) return [];
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, identifier')
        .eq('tenant_id', selectedTenant)
        .eq('status', 'active');
      if (error) throw error;
      return data;
    },
    enabled: !!selectedTenant,
  });

  // Buscar fluxos ativos do tenant selecionado
  const { data: flows } = useQuery({
    queryKey: ['tenant-flows', selectedTenant],
    queryFn: async () => {
      if (!selectedTenant) return [];
      const { data, error } = await supabase
        .from('flows')
        .select('id, name, trigger_config')
        .eq('tenant_id', selectedTenant)
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!selectedTenant,
  });

  // Extrair keyword do fluxo selecionado
  const selectedFlowKeyword = useMemo(() => {
    if (!selectedFlowId || !flows) return '';
    const flow = flows.find(f => f.id === selectedFlowId);
    if (!flow?.trigger_config) return '';
    
    // trigger_config é um array de triggers
    const triggers = flow.trigger_config as Array<{
      type: string;
      config: { keywords?: string[] };
      is_active: boolean;
    }>;
    
    const keywordTrigger = triggers.find(t => t.type === 'keyword' && t.is_active);
    if (keywordTrigger?.config?.keywords?.length) {
      return keywordTrigger.config.keywords[0];
    }
    return '';
  }, [selectedFlowId, flows]);

  // Teste ativo (primeiro em execução)
  const activeTest = useMemo(() => {
    return testRuns?.find(t => t.status === 'running');
  }, [testRuns]);

  // Dados para gráfico de throughput
  const chartData = useMemo(() => {
    return metrics.map((m, i) => ({
      second: i,
      sent: m.messages_sent,
      processed: m.messages_processed,
      failed: m.messages_failed,
      queue: m.queue_size,
      latency: m.latency_avg_ms || 0,
    }));
  }, [metrics]);

  // Handlers
  const handleStartTest = () => {
    if (!selectedTenant || !selectedInbox) return;
    
    // Validação para modo fluxo
    if (flowModeEnabled && !selectedFlowKeyword) {
      return;
    }

    const config: StressTestConfig = {
      tenant_id: selectedTenant,
      inbox_id: selectedInbox,
      messages_per_second: messagesPerSecond,
      duration_seconds: durationSeconds,
      message_types: { text: 80, image: 15, audio: 5 },
      // Configurações de fluxo
      flow_mode_enabled: flowModeEnabled,
      flow_id: flowModeEnabled ? selectedFlowId : undefined,
      trigger_keyword: flowModeEnabled ? selectedFlowKeyword : undefined,
      response_interval_min_ms: responseIntervalMin * 1000,
      response_interval_max_ms: responseIntervalMax * 1000,
      max_interactions_per_flow: maxInteractionsPerFlow,
    };

    startTest.mutate(config, {
      onSuccess: (data) => {
        setSelectedTestId(data.test_id);
        // Navega automaticamente para aba de monitoramento ao iniciar teste
        setActiveTab('monitor');
      },
    });
  };

  const handleStopTest = () => {
    if (activeTest) {
      stopTest.mutate(activeTest.id);
    }
  };

  const handleGenerateContacts = () => {
    if (!selectedTenant) return;
    generateContacts.mutate({ tenant_id: selectedTenant, count: contactCount });
  };

  const handleCleanup = () => {
    if (!selectedTenant) return;
    cleanup.mutate({ tenant_id: selectedTenant, cleanup_type: 'all' });
  };

  // Handler para resetar dados de teste de uma inbox
  const handleResetInbox = () => {
    if (!selectedInbox) return;
    resetInbox.mutate(selectedInbox);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Teste de Carga</h1>
          <p className="text-muted-foreground">
            Simule tráfego de mensagens para validar capacidade do sistema
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetchRuns()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Status Geral */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Fila Atual"
          value={queueSize || 0}
          subtitle="Mensagens pendentes"
          icon={Activity}
          variant={queueSize && queueSize > 100 ? 'warning' : 'default'}
        />
        <StatCard
          title="Teste Ativo"
          value={activeTest ? 'Sim' : 'Não'}
          subtitle={activeTest ? formatDistanceToNow(new Date(activeTest.started_at), { locale: ptBR, addSuffix: true }) : 'Nenhum teste em execução'}
          icon={activeTest ? Loader2 : Zap}
          variant={activeTest ? 'success' : 'default'}
        />
        <StatCard
          title="Total de Testes"
          value={testRuns?.length || 0}
          subtitle="Histórico completo"
          icon={BarChart3}
        />
        <StatCard
          title="Taxa de Sucesso"
          value={testDetails?.stats ? `${(100 - (testDetails.stats.error_rate || 0)).toFixed(1)}%` : 'N/A'}
          subtitle="Teste selecionado"
          icon={CheckCircle}
          variant={testDetails?.stats?.error_rate && testDetails.stats.error_rate > 5 ? 'warning' : 'success'}
        />
      </div>

      {/* Tabs controladas para permitir navegação programática */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="config">Configuração</TabsTrigger>
          <TabsTrigger value="monitor">Monitoramento</TabsTrigger>
          <TabsTrigger value="history">Histórico</TabsTrigger>
        </TabsList>

        {/* Tab de Configuração */}
        <TabsContent value="config" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Configuração do Teste */}
            <Card>
              <CardHeader>
                <CardTitle>Configurar Teste</CardTitle>
                <CardDescription>
                  Defina os parâmetros do teste de carga
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tenant</Label>
                  <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants?.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Inbox</Label>
                  <Select 
                    value={selectedInbox} 
                    onValueChange={setSelectedInbox}
                    disabled={!selectedTenant}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a inbox" />
                    </SelectTrigger>
                    <SelectContent>
                      {inboxes?.map(i => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.name} ({i.identifier})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mensagens/segundo</Label>
                    <Input
                      type="number"
                      min={1}
                      max={500}
                      value={messagesPerSecond}
                      onChange={(e) => setMessagesPerSecond(Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Duração (segundos)</Label>
                    <Input
                      type="number"
                      min={10}
                      max={600}
                      value={durationSeconds}
                      onChange={(e) => setDurationSeconds(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="pt-2 text-sm text-muted-foreground">
                  Total estimado: <strong>{messagesPerSecond * durationSeconds}</strong> mensagens
                </div>

                <Separator />

                {/* Configuração de Teste de Fluxo */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="flex items-center gap-2">
                        <Workflow className="h-4 w-4" />
                        Modo Fluxo
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Simula interação com fluxos automatizados
                      </p>
                    </div>
                    <Switch
                      checked={flowModeEnabled}
                      onCheckedChange={setFlowModeEnabled}
                    />
                  </div>

                  {flowModeEnabled && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                      <div className="space-y-2">
                        <Label>Fluxo para testar</Label>
                        <Select value={selectedFlowId} onValueChange={setSelectedFlowId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um fluxo" />
                          </SelectTrigger>
                          <SelectContent>
                            {flows?.map(f => {
                              const triggers = f.trigger_config as Array<{
                                type: string;
                                config: { keywords?: string[] };
                                is_active: boolean;
                              }> || [];
                              const keywordTrigger = triggers.find(t => t.type === 'keyword' && t.is_active);
                              const keyword = keywordTrigger?.config?.keywords?.[0] || '';
                              
                              return (
                                <SelectItem key={f.id} value={f.id}>
                                  {f.name} {keyword && <span className="text-muted-foreground">(gatilho: "{keyword}")</span>}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {selectedFlowKeyword && (
                          <p className="text-xs text-muted-foreground">
                            Keyword: <Badge variant="secondary">{selectedFlowKeyword}</Badge>
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Intervalo mín. (seg)</Label>
                          <Input
                            type="number"
                            min={1}
                            max={30}
                            value={responseIntervalMin}
                            onChange={(e) => setResponseIntervalMin(Number(e.target.value))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Intervalo máx. (seg)</Label>
                          <Input
                            type="number"
                            min={1}
                            max={60}
                            value={responseIntervalMax}
                            onChange={(e) => setResponseIntervalMax(Number(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Máx. interações por fluxo</Label>
                        <Input
                          type="number"
                          min={1}
                          max={50}
                          value={maxInteractionsPerFlow}
                          onChange={(e) => setMaxInteractionsPerFlow(Number(e.target.value))}
                        />
                        <p className="text-xs text-muted-foreground">
                          Limite de cliques simulados por contato
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex gap-2">
                  {activeTest ? (
                    <Button 
                      variant="destructive" 
                      className="flex-1"
                      onClick={handleStopTest}
                      disabled={stopTest.isPending}
                    >
                      <Square className="mr-2 h-4 w-4" />
                      Parar Teste
                    </Button>
                  ) : (
                    <Button 
                      className="flex-1"
                      onClick={handleStartTest}
                      disabled={!selectedTenant || !selectedInbox || startTest.isPending}
                    >
                      {startTest.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="mr-2 h-4 w-4" />
                      )}
                      Iniciar Teste
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Gerenciamento de Contatos */}
            <Card>
              <CardHeader>
                <CardTitle>Contatos de Teste</CardTitle>
                <CardDescription>
                  Gere contatos fake para o teste de carga
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Quantidade de contatos</Label>
                  <Input
                    type="number"
                    min={100}
                    max={50000}
                    value={contactCount}
                    onChange={(e) => setContactCount(Number(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Números sequenciais: 5500000000001 até 55000000{String(contactCount).padStart(5, '0')}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={handleGenerateContacts}
                      disabled={!selectedTenant || generateContacts.isPending}
                    >
                      {generateContacts.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Users className="mr-2 h-4 w-4" />
                      )}
                      Gerar Contatos
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleCleanup}
                      disabled={!selectedTenant || cleanup.isPending}
                    >
                      {cleanup.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}
                      Limpar Tudo
                    </Button>
                  </div>
                  {/* Botão de Reset por Inbox */}
                  <Button 
                    variant="outline"
                    onClick={handleResetInbox}
                    disabled={!selectedInbox || resetInbox.isPending}
                    className="w-full border-orange-500/50 text-orange-600 hover:bg-orange-500/10 hover:text-orange-700"
                  >
                    {resetInbox.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="mr-2 h-4 w-4" />
                    )}
                    Resetar Inbox (manter contatos)
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Remove mensagens, conversas e execuções de fluxo. Mantém contatos para reutilização.
                  </p>
                </div>

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-medium">Distribuição de Mensagens</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Texto</span>
                      <span className="text-muted-foreground">80%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Imagens</span>
                      <span className="text-muted-foreground">15%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Áudio</span>
                      <span className="text-muted-foreground">5%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab de Monitoramento */}
        <TabsContent value="monitor" className="space-y-4">
          {selectedTestId && testDetails ? (
            <>
              {/* Status do Teste */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Teste: {selectedTestId.substring(0, 8)}</CardTitle>
                      <CardDescription>
                        Iniciado {formatDistanceToNow(new Date(testDetails.test.started_at), { locale: ptBR, addSuffix: true })}
                      </CardDescription>
                    </div>
                    <TestStatusBadge status={testDetails.test.status} />
                  </div>
                </CardHeader>
                <CardContent>
                  {testDetails.stats && (
                    <div className="grid gap-4 md:grid-cols-5">
                      <div>
                        <div className="text-2xl font-bold">{testDetails.stats.total_sent}</div>
                        <div className="text-xs text-muted-foreground">Enviadas</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-500">{testDetails.stats.total_processed}</div>
                        <div className="text-xs text-muted-foreground">Processadas</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-red-500">{testDetails.stats.total_failed}</div>
                        <div className="text-xs text-muted-foreground">Falhas</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{testDetails.stats.throughput_per_second.toFixed(1)}</div>
                        <div className="text-xs text-muted-foreground">Msgs/segundo</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{testDetails.stats.avg_latency_ms.toFixed(0)}ms</div>
                        <div className="text-xs text-muted-foreground">Latência média</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Gráfico de Throughput */}
              <Card>
                <CardHeader>
                  <CardTitle>Throughput em Tempo Real</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="second" label={{ value: 'Segundos', position: 'bottom' }} />
                        <YAxis label={{ value: 'Mensagens', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Area 
                          type="monotone" 
                          dataKey="sent" 
                          stackId="1"
                          stroke="#3b82f6" 
                          fill="#3b82f6" 
                          fillOpacity={0.6}
                          name="Enviadas"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="processed" 
                          stackId="2"
                          stroke="#22c55e" 
                          fill="#22c55e" 
                          fillOpacity={0.6}
                          name="Processadas"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="failed" 
                          stackId="3"
                          stroke="#ef4444" 
                          fill="#ef4444" 
                          fillOpacity={0.6}
                          name="Falhas"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Gráfico de Latência */}
              <Card>
                <CardHeader>
                  <CardTitle>Latência</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="second" />
                        <YAxis label={{ value: 'ms', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Line 
                          type="monotone" 
                          dataKey="latency" 
                          stroke="#f59e0b" 
                          strokeWidth={2}
                          dot={false}
                          name="Latência (ms)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Activity className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">Nenhum teste selecionado</h3>
                <p className="text-sm text-muted-foreground text-center">
                  Inicie um novo teste ou selecione um do histórico para visualizar métricas
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab de Histórico */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Testes</CardTitle>
              <CardDescription>
                Últimos testes de carga executados
              </CardDescription>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : testRuns && testRuns.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Iniciado</TableHead>
                      <TableHead>Duração</TableHead>
                      <TableHead>Msgs/s</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {testRuns.map((run) => (
                      <TableRow 
                        key={run.id}
                        className={selectedTestId === run.id ? 'bg-muted/50' : ''}
                      >
                        <TableCell className="font-mono text-xs">
                          {run.id.substring(0, 8)}
                        </TableCell>
                        <TableCell>
                          <TestStatusBadge status={run.status} />
                        </TableCell>
                        <TableCell>
                          {format(new Date(run.started_at), 'dd/MM HH:mm', { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          {run.ended_at 
                            ? `${Math.round((new Date(run.ended_at).getTime() - new Date(run.started_at).getTime()) / 1000)}s`
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          {(run.config as StressTestConfig)?.messages_per_second || '-'}
                        </TableCell>
                        <TableCell>
                          {(run.results as Record<string, number>)?.total_sent || '-'}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedTestId(run.id);
                              // Navega para aba de monitoramento ao clicar em Ver
                              setActiveTab('monitor');
                            }}
                          >
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum teste executado ainda
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
